<template>
  <label
    v-click-outside="onBlur"
    :for="id"
  >
    <textarea
      :id="id"
      ref="textarea"
      :value="value"
      :placeholder="placeholder"
      :maxlength="maxlength"
      :rows="rows"
      @input="$emit('onInput', $event.target.value)"
      @focus="onFocus"
      @focusout="onFocusout"
    />
    <slot />
  </label>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import { vOnClickOutside } from '@vueuse/components';

  export default defineNuxtComponent({
    name: 'TextAreaCustom',

    directives: {
      clickOutside: vOnClickOutside,
    },

    props: {
      id: {
        type: String as PropType<string>,
        required: true,
      },

      value: {
        type: String as PropType<string>,
        required: true,
      },

      placeholder: {
        type: String as PropType<string>,
        default: '',
      },

      maxlength: {
        type: Number as PropType<number>,
        default: -1,
      },

      rows: {
        type: Number as PropType<number>,
        default: 1,
      },
    },

    emits: ['onInput', 'on-focus', 'on-blur', 'on-focusout'],

    data: () => ({
      startHeight: 0,
    }),

    watch: {
      value() {
        this.resizeTextarea();
      },

      rows() {
        this.resizeTextarea();
      },
    },

    mounted() {
      this.resizeTextarea();
    },

    methods: {
      async resizeTextarea(): Promise<void> {
        await this.$nextTick();
        const textarea = this.$refs.textarea as HTMLElement;
        this.startHeight = !this.startHeight
          ? parseInt(textarea.style.height, 10)
          : this.startHeight;
        textarea.style.height = 'auto';
        textarea.style.removeProperty('height');
        textarea.style.height =
          textarea.scrollHeight < this.startHeight
            ? `${this.startHeight}px`
            : `${textarea.scrollHeight}px`;
      },

      onFocus() {
        this.$emit('on-focus');
      },

      onBlur() {
        this.$emit('on-blur');
      },

      onFocusout() {
        this.$emit('on-focusout');
      },
    },
  });
</script>

<style lang="scss" scoped>
  textarea {
    font-family: $text-font--1;
    @include normalText;
    width: 100%;
    font-weight: 400;
    resize: none;
    border: none;
    padding: 12px 16px;
    border-radius: 4px;
    outline: none;
  }
</style>
